export const claims = {
  pageTitle: {
    allClaimsSubmissions: 'All Claims Submissions',
    claimsSubmissions: 'Claims Submissions'
  },
  actions: {
    scheduleReport: 'Schedule Report',
    viewScheduledReports: 'View Scheduled Reports',
    loadForExport: 'Load for Export',
    scheduleClaimsSubmissions: (count: number | string) => `Schedule ${count} Claims Submissions`,
    rescheduleClaims: (count: number | string) => `Reschedule ${count} Claims`,
    exportSubmissions: 'Export Submissions',
    unscheduleClaimsSubmissions: (count: number | string) => `Unschedule ${count} Claims Submissions`,
    initiateSubmission: (count: number) =>
      count > 0 ? `Initiate ${count} Selected Submission${count > 1 ? 's' : ''}` : 'Initiate Submission',
    initiateClaimsSync: 'Initiate Claims Sync',
    exportFiltered: 'Export Filtered',
    exportAll: 'Export All for Month'
  },
  tabs: {
    claims: 'Charges',
    submissions: 'Submissions',
    rcm: 'RCM'
  },
  dialogs: {
    scheduleReportTitle: 'Schedule Claims Report',
    scheduledReportsTitle: 'Scheduled Reports',
    icdCodesAndDescriptionsTitle: 'ICD10 Codes and Descriptions',
    confirmStatusChangeTitle: 'Confirm Status Change',
    confirmStatusChangeSingleMessage: (status: string) =>
      `Are you sure you want to change the status of this billing submission to <b>'${status}'</b>?`,
    confirmStatusChangeBulkMessage: (status: string) =>
      `Are you sure you want to change the status of selected billing submission(s) to <b>'${status}'</b>?`,
    selectOrganizationsTitle: 'Select pharmacies and appropriate service dates',
    selectOrganizationsDescription:
      'Please add pharmacies and select the service date for which you want to submit claim submissions for each pharmacy.',
    confirmInitiateSubmissionTitle: 'Submit claims to Athena?',
    // count 0 means nothing is selected, which submits every scheduled claim
    // for the month, not zero claims.
    confirmInitiateSubmissionMessage: (count: number) =>
      count > 0
        ? `This submits <b>${count} selected claim${count === 1 ? '' : 's'}</b> to Athena. It cannot be undone from this screen. Continue?`
        : 'This submits <b>every scheduled claim</b> for the selected month to Athena. It cannot be undone from this screen. Continue?',
    rcmMonthBasis: (monthYear: string, basis: string) =>
      basis === 'BILLING_MONTH'
        ? `Claims from the ${monthYear} billing run. Its monthly claims are dated early the following month.`
        : `Claims with a date of service in ${monthYear}`,
    confirmClaimsSyncTitle: 'Start claims sync?',
    confirmClaimsSyncMessage: (monthYear: string, basis: string) =>
      `This queues a background job for every submitted claim ${
        basis === 'BILLING_MONTH' ? `from the ${monthYear} billing run` : `with a date of service in ${monthYear}`
      }. It calls Athena twice per claim, for the payment ledger and the claim header (paid, adjusted and outstanding amounts, and each payer's status). Nothing is submitted or changed at Athena. Continue?`
  },
  snackbars: {
    scheduledSuccess: 'Claims Report scheduled successfully',
    scheduleFailed: 'Failed to schedule Claims Report',
    selectAtLeastOneReportType: 'Please select at least one report type to schedule.',
    fetchSubmissionsFailed: 'Unable to retrieve claim submissions. Please contact support for assistance.',
    unexpectedError: 'Unexpected error occurred.',
    updateSubmissionsFailed: 'Unable to update the selected claim submission(s). Please contact support.',
    claimsSyncQueued: 'Claims sync queued. Figures will populate as the job progresses.',
    claimsSyncFailed: 'Unable to queue the claims sync. Please contact support.',
    fetchRcmFailed: 'Unable to retrieve RCM data. Please contact support for assistance.',
    noRowsToExport: 'There is nothing to export for the selected filters.',
    exportRequested: 'Export requested. Progress and the download show above the grid.',
    scheduleStarted: (month: string) => `Scheduling started for ${month}. Progress shows above the grid.`,
    submitStarted: (month: string) => `Submission started for ${month}. Progress shows above the grid.`,
    outcomeUnknown: 'The server did not confirm the request, so the outcome is unknown. Check the progress banner before trying again.',
    exportReady: (count: number) => `Exported ${count.toLocaleString()} claim${count === 1 ? '' : 's'}.`
  },
  listFilters: {
    failureTypes: 'Failure type',
    allFailureTypes: 'All',
    categories: {
      PHARMACY_CONFIGURATION: 'Pharmacy Athena setup',
      NO_PRIMARY_INSURANCE: 'No primary insurance',
      PATIENT_NOT_IN_DEPARTMENT: 'Patient not in Athena department',
      PATIENT_NOT_FOUND: 'Patient not found in Athena',
      DIAGNOSIS_OR_CHARGES: 'Diagnosis or charge codes',
      REASON_NOT_RECORDED: 'Athena error (reason not recorded)',
      OTHER: 'Other'
    } as Record<string, string>
  },
  runs: {
    exportTitle: (months: string) => `Exporting claim submissions, ${months}`,
    exportResult: (count: number) => `${count.toLocaleString()} claim submissions exported`,
    downloadCsv: 'Download CSV',
    scheduleTitle: (month: string) => `Scheduling claims for ${month}`,
    submitTitle: (month: string) => `Submitting claims${month ? ` for ${month}` : ''} to Athena`,
    status: {
      QUEUED: 'Queued',
      RUNNING: 'In progress',
      COMPLETED: 'Completed',
      FAILED: 'Failed'
    } as Record<string, string>,
    started: (when: string, who: string | null) => `Started ${when}${who ? ` by ${who}` : ''}`,
    finished: (when: string) => `finished ${when}`,
    progress: (processed: number, total: number) => `${processed.toLocaleString()} of ${total.toLocaleString()}`,
    waitingToStart: 'Waiting for a worker to pick this up...',
    counting: 'Working out how many claims to create...',
    scheduleResult: (succeeded: number, failed: number, skipped: number) =>
      `${succeeded.toLocaleString()} scheduled, ${failed.toLocaleString()} failed${
        skipped ? `, ${skipped.toLocaleString()} already existed` : ''
      }`,
    submitResult: (succeeded: number, failed: number) => `${succeeded.toLocaleString()} submitted, ${failed.toLocaleString()} failed`,
    blockedSchedule: 'Scheduling is already running for this month',
    blockedSubmit: 'A submission is already running'
  },
  scheduleDialog: {
    title: (month: string) => `Schedule claims for ${month}`,
    serviceDateLabel: 'Date of service',
    serviceDateHelp: (codes: string) => `Used for ${codes}. Visit codes keep the visit or follow-up date.`,
    previousHint: (date: string) => `The previous month was scheduled with ${date}.`,
    serviceDateOutsideNextMonth: (month: string) =>
      `Monthly claims are usually dated early in ${month}. Check this date before scheduling.`,
    searchPlaceholder: 'Search pharmacies',
    selectAllReady: (count: number) => `Select all ready (${count})`,
    clearSelection: 'Clear',
    columns: {
      pharmacy: 'Pharmacy',
      department: 'Athena Dept',
      claims: 'Est. Claims',
      patients: 'Patients',
      billings: 'Billings',
      serviceDate: 'Date of Service',
      issues: 'Issues'
    },
    usesDefaultDate: 'Default',
    nothingToSchedule: 'Every billing for this month already has a claim. Nothing left to schedule.',
    loadFailed: 'Could not load the pharmacies for this month. Close the dialog and try again.',
    loading: 'Loading pharmacies and unscheduled billings...',
    footer: (pharmacies: number, claims: number, date: string) =>
      `${pharmacies.toLocaleString()} pharmac${pharmacies === 1 ? 'y' : 'ies'}, about ${claims.toLocaleString()} claims, dated ${date}`,
    review: 'Review',
    back: 'Back',
    schedule: 'Schedule',
    cancel: 'Cancel',
    reviewTitle: 'Review before scheduling',
    reviewSummary: (pharmacies: number, claims: number, patients: number) =>
      `This creates about <b>${claims.toLocaleString()} claims</b> for ${patients.toLocaleString()} patients across ${pharmacies.toLocaleString()} pharmac${
        pharmacies === 1 ? 'y' : 'ies'
      }.`,
    reviewDate: (date: string) => `Date of service: <b>${date}</b>`,
    reviewOverrides: (count: number) => `${count} pharmac${count === 1 ? 'y uses' : 'ies use'} a different date:`,
    reviewNote:
      'Scheduling runs in the background and shows its progress on the Submissions tab. Nothing is sent to Athena until you initiate submission.'
  },
  exportDialog: {
    title: 'Export claim submissions',
    description:
      'Builds a CSV of every claim submission with the chosen statuses in the billing months below. You can keep working; progress shows on the Submissions tab.',
    fromMonth: 'From billing month',
    toMonth: 'To billing month',
    quickPicks: 'Quick pick',
    lastMonths: (count: number) => `Last ${count} months`,
    statuses: 'Statuses to include',
    scopeAll: 'Every pharmacy',
    scopeOwn: 'This pharmacy only',
    pickAStatus: 'Pick at least one status.',
    tooManyMonths: 'Pick at most 36 billing months.',
    request: 'Request export',
    cancel: 'Cancel'
  },
  reportTypes: {
    allRpmClaimsReport: 'RPM Claims Report (All Organizations)',
    allCcmClaimsReport: 'CCM Claims Report (All Organizations)',
    rpmClaimsReport: 'RPM Claims Report (Current Organization)',
    ccmClaimsReport: 'CCM Claims Report (Current Organization)'
  },
  emptyState: {
    noDataForCurrentMonth: 'The data for the current month has not been generated.',
    scheduleToGenerate: 'Click the following button to schedule report for the current month and generate data.'
  },
  notProvided: 'Not Provided',
  csvExport: {
    ehrId: 'EHR ID',
    dob: 'DOB',
    firstName: 'FIRST NAME',
    lastName: 'LAST NAME',
    type: 'TYPE',
    icd10Code: 'ICD10 CODE',
    icd10Description: 'ICD10 DESCRIPTION',
    dateOfService: 'DATE OF SERVICE',
    providerName: 'PROVIDER NAME',
    providerNPI: 'PROVIDER NPI',
    company: 'PHARMACY',
    export: 'Export',
    loadingLabel: 'Loading CSV...',
    allOrgFilename: (date: string) => `RPM Claims - ${date}.csv`,
    currentOrgFilename: (name: string, date: string) => `${name} - RPM Claims - ${date}.csv`
  },
  grid: {
    ehrId: 'EHR Id',
    patientName: 'Patient Name',
    company: 'Pharmacy',
    dateOfBirth: 'Date of Birth',
    dateEnrolled: 'Date Enrolled',
    type: 'Type',
    serviceType: 'Service Type',
    icd10Code: 'ICD10 Code',
    icd10Description: 'ICD10 Description',
    dateOfService: 'Date of Service',
    providerName: 'Provider Name',
    providerNPI: 'Provider NPI'
  },
  submissionsGrid: {
    patientName: 'Patient Name',
    billingKey: 'Billing Key',
    organizationName: 'Pharmacy Name',
    providerName: 'Provider Name',
    scheduledOn: 'Scheduled On',
    serviceDate: 'Service Date',
    serviceType: 'Service Type',
    processedData: 'Processed Data',
    statusDetails: 'Status Details',
    performedOn: 'Performed On',
    action: 'Action',
    billingMonth: 'Billing Month',
    serviceMonth: 'Service Month',
    failureType: 'Failure Type'
  }
} as const;
